import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  Image as ImageIcon,
  Tag,
  DollarSign,
  Activity,
  Clock,
  Receipt,
  Megaphone,
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getAsset } from '@/client/utils/assets';
import { useListingQuery } from '@/client/api/listing-management/listing.query';
import SuggestEditDialog from '@/client/components/admin/ListingManagement/SuggestEditDialog';
import {
  useEntityDetailQuery,
  useEntityPricesQuery,
  useEntityEngagementQuery,
  useEntityHistoryQuery,
  useEntityPaymentsQuery,
  useEntityPromotionsQuery,
  useEntityAttributesQuery,
  useEntityAssetsQuery,
  EntityAsset,
} from '@/client/api/listing-management/entity.query';

import { asArray, truthy, money, fmtDate } from './ListingDetail/helpers';
import type { TabId } from './ListingDetail/types';
import { useListingActions } from './ListingDetail/useListingActions';
import { GoogleSearchPanel } from './ListingDetail/GoogleSearchPanel';
import { ListingDetailSkeleton } from './ListingDetail/LoadingSkeleton';
import { ListingHero } from './ListingDetail/ListingHero';
import { ModerationBar } from './ListingDetail/ModerationBar';
import { OverviewTab } from './ListingDetail/OverviewTab';
import { TagsTab } from './ListingDetail/TagsTab';
import { MediaManager } from './ListingDetail/MediaManager';
import { VirtualToursSection } from './ListingDetail/VirtualToursSection';
import { SimpleTable } from './ListingDetail/SimpleTable';
import { HistoryTimeline } from './ListingDetail/HistoryTimeline';
import { EngagementTrendChart, type EngagementGranularity } from './ListingDetail/EngagementTrendChart';

/** Rolling 12 months: the 1st of the month 11 back, through today. */
const lastTwelveMonths = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start_date: iso(start), end_date: iso(today) };
};
import { EngagementView } from './ListingDetail/EngagementView';
import { PromotionView } from './ListingDetail/PromotionView';
import { ConfirmDialog } from './ListingDetail/ConfirmDialog';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'media', label: 'Media', icon: ImageIcon },
  { id: 'tags', label: 'Tags', icon: Tag },
  { id: 'pricing', label: 'Pricing', icon: DollarSign },
  { id: 'engagement', label: 'Engagement', icon: Activity },
  { id: 'history', label: 'History', icon: Clock },
  { id: 'payments', label: 'Payments', icon: Receipt },
  { id: 'promotions', label: 'Promotions', icon: Megaphone },
] as const;

const ListingDetailPage: React.FC = () => {
  const { eid: eidParam } = useParams();
  const eid = Number(eidParam);
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [suggestOpen, setSuggestOpen] = useState(false);

  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Listings', href: '/admin/listings' },
    { label: eidParam ? `#${eidParam}` : 'Detail' },
  ]);

  const { data: entity, isLoading, isError, refetch: refetchDetail } =
    useEntityDetailQuery(eid, !!eid);

  // Sub-resources load lazily per active tab (overview needs attributes/prices).
  const prices = useEntityPricesQuery(eid, tab === 'pricing' || tab === 'overview');
  const attributes = useEntityAttributesQuery(eid, tab === 'overview');
  // Monthly opens on a 12-month window of its own; daily/weekly roll up the
  // API's default 30-day daily series. Only the visible one is fetched — both
  // responses carry the same (date-independent) counts block.
  const [engView, setEngView] = useState<EngagementGranularity>('monthly');
  const engagement = useEntityEngagementQuery(eid, tab === 'engagement' && engView !== 'monthly');
  const engagementMonthly = useEntityEngagementQuery(
    eid,
    tab === 'engagement' && engView === 'monthly',
    { granularity: 'monthly', ...lastTwelveMonths() },
  );
  const engagementSource = engView === 'monthly' ? engagementMonthly : engagement;
  const history = useEntityHistoryQuery(eid, tab === 'history');
  const payments = useEntityPaymentsQuery(eid, tab === 'payments');
  const promotions = useEntityPromotionsQuery(eid, tab === 'promotions');
  const assets = useEntityAssetsQuery(eid, tab === 'media');

  const assetPeek = useListingQuery(1, { eid, per_page: 1, visibility: 'all' });
  const peekAssets = asArray<any>(assetPeek.data?.entities?.[0]?.Assets);

  const actions = useListingActions({
    eid,
    refetchDetail,
    refetchPromotions: promotions.refetch,
    refetchAssets: assets.refetch,
    onDuplicated: (newEid) => navigate(`/admin/listings/${newEid}`),
  });

  const e = (entity ?? {}) as Record<string, any>;
  const status: string = (e.status ?? '').toUpperCase();
  const isDeleted = truthy(e.deleted) || status === 'DELETED';

  const priceList = asArray<any>(prices.data?.prices);
  const primaryPrice = priceList[0]?.price;

  // Prefer real uploaded assets (peek list → assets endpoint → detail payload),
  // then fall back to product-match (model_json) photos.
  const images: string[] = useMemo(() => {
    const out: string[] = [];
    const push = (a: any) => {
      if (!a) return;
      const u = typeof a === 'string' ? a : getAsset(a);
      if (u) out.push(u);
    };
    peekAssets.filter((a) => !truthy(a.deleted)).forEach(push);
    asArray<any>(assets.data?.assets).filter((a) => !truthy(a.deleted)).forEach(push);
    asArray<any>(e.Assets).forEach(push);
    asArray<any>(e.assets).forEach(push);
    const mj = e.model_json;
    if (mj && Array.isArray(mj.images)) mj.images.forEach((u: string) => u && out.push(u));
    else if (mj?.image) out.push(mj.image);
    return Array.from(new Set(out.filter(Boolean)));
  }, [e, assets.data, peekAssets]);

  const historyRows = asArray<any>(history.data?.history);
  const paymentRows = asArray<any>(payments.data?.payments);
  const attributeRows = asArray<any>(attributes.data?.attributes);

  if (isLoading) return <ListingDetailSkeleton />;

  if (isError || !entity) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <button
          onClick={() => navigate('/admin/listings')}
          className="mb-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" /> Back to listings
        </button>
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Could not load listing #{eidParam}.
        </div>
      </div>
    );
  }

  const name = e.name_basic || e.name || e.name_marketing || `Listing #${eid}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <ListingHero
          entity={e}
          eid={eid}
          name={name}
          status={status}
          isDeleted={isDeleted}
          coverImage={images[0]}
          primaryPrice={primaryPrice}
          onBack={() => navigate('/admin/listings')}
        />

        <ModerationBar
          entity={e}
          status={status}
          isDeleted={isDeleted}
          busy={actions.busy}
          setFlagPending={actions.pending.setFlag}
          boostPending={actions.pending.boost}
          undeletePending={actions.pending.undelete}
          onConfirm={actions.setConfirm}
          onSuggestEdit={() => setSuggestOpen(true)}
        />

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-2">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                  tab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === 'overview' && (
              <div className="space-y-6">
                <OverviewTab
                  description={e.description}
                  attributesLoading={attributes.isLoading}
                  attributes={attributeRows}
                />
                <GoogleSearchPanel eid={eid} />
              </div>
            )}

            {tab === 'tags' && <TagsTab eid={eid} etid={e.etid ?? e.etid_root ?? null} />}

            {tab === 'media' && (
              <MediaManager
                loading={assets.isLoading}
                assets={asArray<EntityAsset>(assets.data?.assets)}
                fallbackImages={images}
                busyAsid={actions.assetBusyAsid}
                onDelete={(asid) => actions.setConfirm({ kind: 'asset-delete', asid })}
                onRestore={(asid) => actions.setConfirm({ kind: 'asset-restore', asid })}
              />
            )}

            {tab === 'media' && <VirtualToursSection eid={eid} />}

            {tab === 'pricing' && (
              <SimpleTable
                loading={prices.isLoading}
                empty={priceList.length === 0}
                emptyText="No prices set."
                head={['Price', 'Currency', 'Type', 'Price ID']}
                rows={priceList.map((p: any) => [
                  money(p.price),
                  <div>{p.ccode != null && p.ccode !== '' && <div className="text-xs text-gray-400">{p.ccode}</div>}</div>,
                  <div>{p.price_shape != null && p.price_shape !== '' && <div className="text-xs text-gray-400">{p.price_shape}</div>}</div>,
                  <span className="font-mono text-xs">{p.pricid ?? p.id ?? '—'}</span>,
                ])}
              />
            )}

            {tab === 'engagement' && (
              <div className="space-y-6">
                <EngagementTrendChart
                  loading={engagementSource.isLoading}
                  timeseries={engagementSource.data?.engagement_timeseries as any}
                  view={engView}
                  onView={setEngView}
                />
                <EngagementView loading={engagementSource.isLoading} data={engagementSource.data?.engagement} />
              </div>
            )}

            {tab === 'history' && <HistoryTimeline loading={history.isLoading} rows={historyRows} />}

            {tab === 'payments' && (
              <SimpleTable
                loading={payments.isLoading}
                empty={paymentRows.length === 0}
                emptyText="No payments recorded against this listing."
                head={['Amount', 'Status', 'Reference', 'Date']}
                rows={paymentRows.map((p: any) => [
                  money(p.amount ?? p.total),
                  p.status ?? '—',
                  <span className="font-mono text-xs">{p.reference ?? p.payid ?? p.id ?? '—'}</span>,
                  fmtDate(p.date_added || p.created_at),
                ])}
              />
            )}

            {tab === 'promotions' && <PromotionView loading={promotions.isLoading} data={promotions.data?.promotion} />}
          </div>
        </div>
      </div>

      {suggestOpen && (
        <SuggestEditDialog
          open={suggestOpen}
          eid={eid}
          name={name}
          ownerUid={e.owner?.uid ?? e.uid ?? null}
          ownerOid={e.oid ?? null}
          ownerName={e.owner ? [e.owner.first_name, e.owner.last_name].filter(Boolean).join(' ') || null : null}
          onClose={() => setSuggestOpen(false)}
        />
      )}

      <ConfirmDialog
        confirm={actions.confirm}
        name={name}
        busy={actions.busy}
        onCancel={() => actions.setConfirm(null)}
        onConfirm={actions.runConfirm}
      />
    </div>
  );
};

export default ListingDetailPage;
